/**
 * Die Abspielleiste und der große Player. (04.09.2026)
 *
 * ## Wozu
 *
 * Martin: *„Wenn wir nur einen Player hätten auf der jeweiligen Seite und
 * einen Schnittpunkt zu dem Player. Wenn ich im Alltag eine Sequenz öffne,
 * dass dort ein Button ist — und wenn ich draufklicke, vergrößert sich in
 * einem Modal der Player mit Überschrift, herausgehoben."*
 *
 * Die Leiste ist der Zwischenschritt, den sein Entwurf noch nicht hatte: Wer
 * kurz in eine Spur hineinhört und weiterliest, will kein Fenster im Weg —
 * aber sehen, dass etwas läuft. Also trägt die Leiste die Wahrheit ständig,
 * und das Modal ist die Vergrößerung auf Wunsch.
 *
 * ## Woher sie weiß, was läuft
 *
 * Aus dem {@link ../ton-leitstand.js Ton-Leitstand} — nicht aus einem
 * eigenen Zustand. Deshalb kann hier nie etwas anderes stehen als das, was
 * wirklich klingt: Genau diese Trennung war Martins zweites Problem (*„man
 * hört zwar eine Stimme, aber der Player läuft offensichtlich nicht"*).
 *
 * ## Mini-Player (19.10.2026)
 *
 * Martin: Läuft eine Vorlesung, soll sie unten als kompakter Player auf JEDER Seite stehen — Titel, Pause/Weiter,
 * zurück/vor, Schieber mit Zeit oder Abschnitt, Stopp, Sprung zur Karte im Chat. Das ist diese Leiste, nicht eine
 * zweite: Sie zeichnet aus zwei Quellen und bedient sie nur über deren eigene Griffe —
 *   1. der Ton-Leitstand (Spur, Erzählstimme, Musik …): Schieber = Sekunden, Schritte = ±15 s;
 *   2. die aktive Vorlese-Quelle der Chat-Antworten (vorlese-quelle.ts, dieselbe wie die Vorlese-Karte): Schieber =
 *      Abschnitt, Schritte = ein Abschnitt, Titel springt zur Karte im Chat.
 * Der Leitstand geht vor, wenn beide etwas melden (er ist die ausdrückliche Bühne).
 *
 * ⚠️ Diese Datei baut NUR Anzeige. Sie startet nichts, sie hält nichts an —
 * das tut ausschließlich der Leitstand über die Quellen selbst. Wer hier
 * anfängt, ein `<audio>` anzufassen, hat den sechsten Ton-Weg erfunden.
 */
import {
  aktuellerTon,
  beende,
  beobachte,
  halteAn,
  setzeFort,
  springe,
  type TonArt,
  type TonStand,
} from "./ton-leitstand.js";
import { gesteuert } from "./ruckel-schalter.js";
import { aktiveVorleseQuelle, beobachteVorlesen, teilText, type VorleseQuelle } from "./vorlese-quelle.js";

const PLAY =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
const STOPP =
  '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>';
const PAUSE =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M6 19h4V5H6zm8-14v14h4V5z"/></svg>';

const SPINNER =
  '<svg class="tonleiste-spinner" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor"><path d="M12 4a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6V4z"/></svg>';
const ABSCHNITT_ZURUECK =
  '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M6 6h2v12H6zM9.5 12 18 18V6z"/></svg>';
/** Aufklappen der übrigen Bedienung (dreht sich offen um, style.css). */
const MEHR =
  '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M7.4 15.4 12 10.8l4.6 4.6L18 14l-6-6-6 6z"/></svg>';
const ABSCHNITT_VOR =
  '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M16 6h2v12h-2zM6 18l8.5-6L6 6z"/></svg>';

/** Ein Zeichen je Art — damit man ohne Lesen erkennt, was da klingt. */
const ZEICHEN: Record<TonArt, string> = {
  spur: '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M12 4v10"/><path d="M8 7v4"/><path d="M16 7v4"/><circle cx="12" cy="18" r="2.5"/></svg>',
  vorlesen: '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M4 9v6h4l5 4V5L8 9H4Z"/><path d="M17 9.5a4 4 0 0 1 0 5"/></svg>',
  musik: '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M9 18V6l10-2v12"/><circle cx="6.5" cy="18" r="2.5"/><circle cx="16.5" cy="16" r="2.5"/></svg>',
  anruf: '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A15 15 0 0 1 4 5a1 1 0 0 1 1-1Z"/></svg>',
};

/** Ein Wort je Art — für Menschen, die nicht sehen. */
const ANSAGE: Record<TonArt, string> = {
  spur: "Aufnahme",
  vorlesen: "Vorlesen",
  musik: "Musik",
  anruf: "Anruf",
};

function uhr(sek: number): string {
  if (!Number.isFinite(sek) || sek < 0) sek = 0;
  const m = Math.floor(sek / 60);
  const s = Math.floor(sek % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

/** Zeitangabe, die auch ohne bekannte Gesamtlänge ehrlich bleibt. */
function zeitText(stand: TonStand): string {
  const f = stand.fortschritt;
  if (!f) return "";
  return f.dauer > 0 ? `${uhr(f.position)} / ${uhr(f.dauer)}` : uhr(f.position);
}

let leiste: HTMLElement | null = null;
let modal: HTMLElement | null = null;
let ticker: ReturnType<typeof setInterval> | null = null;
let hoehenBeobachter: ResizeObserver | null = null;
/** Der Finger zieht gerade am Schieber: die Uhr schreibt ihn dann nicht zurück. */
let schieberZieht = false;

/** Sprung in Sekunden der Schritt-Knöpfe, wenn die Quelle Sekunden kennt. */
const SCHRITT_SEK = 15;

/**
 * Was die Leiste zeichnet — aus EINER der beiden Quellen gebaut (Leitstand oder Vorlese-Quelle) und mit deren
 * eigenen Griffen bedient. Die Leiste hält davon nichts fest; jede Zeichnung baut es neu.
 */
interface Ansicht {
  art: TonArt;
  titel: string;
  klingt: boolean;
  /** Der Start läuft noch (Spinner statt Pause; Tipp bricht ab). */
  laedt: boolean;
  fortsetzbar: boolean;
  zeit: string;
  regler: { wert: number; max: number; text: string; label: string } | null;
  /** Wie die Schritt-Knöpfe heißen — null: die Quelle kann nicht springen. */
  schritt: { art: "sekunden" | "abschnitt"; zurueck: string; vor: string } | null;
  umschalten: () => void;
  stopp: () => void;
  gehe: (richtung: 1 | -1) => void;
  setze: (wert: number) => void;
  /** Titel-Fläche tippen: ohne Angabe öffnet sich der große Player. */
  zurKarte?: () => boolean;
}

function ansichtAusTon(stand: TonStand): Ansicht {
  const f = stand.fortschritt;
  const mitSchieber = stand.springbar && !!f && f.dauer > 0 && f.geschaetzt !== true;
  return {
    art: stand.art,
    titel: stand.titel,
    klingt: stand.klingt,
    laedt: false,
    fortsetzbar: stand.fortsetzbar,
    zeit: zeitText(stand),
    regler:
      mitSchieber && f
        ? {
            wert: Math.min(f.position, f.dauer),
            max: f.dauer,
            text: `${uhr(f.position)} von ${uhr(f.dauer)}`,
            label: "Position",
          }
        : null,
    schritt: stand.springbar ? { art: "sekunden", zurueck: `${SCHRITT_SEK} Sekunden zurück`, vor: `${SCHRITT_SEK} Sekunden vor` } : null,
    umschalten: () => (aktuellerTon()?.klingt ? halteAn() : setzeFort()),
    stopp: beende,
    gehe: (r) => springe(r * SCHRITT_SEK),
    setze: (wert) => {
      const pos = aktuellerTon()?.fortschritt?.position ?? 0;
      springe(wert - pos);
    },
  };
}

function ansichtAusVorlesen(q: VorleseQuelle): Ansicht {
  const z = q.zustand();
  const starts = q.starts();
  const i = q.abschnitt();
  const text = teilText(i, starts.length);
  // Streaming kennt keine Gesamtdauer: ehrlich nur die gehörte Zeit seit Start (+ Abschnitt), keine „/ gesamt“.
  const hoert = z === "playing" || z === "paused" || z === "loading"; // beim Laden steht die Zeit still, sie verschwindet nicht
  const zeit = hoert ? uhr(q.laufzeitSek()) : "";
  const zeile = [zeit, starts.length > 1 ? text : ""].filter(Boolean).join(" · ");
  return {
    art: "vorlesen",
    titel: q.titel(),
    klingt: z === "playing",
    laedt: z === "loading",
    fortsetzbar: true,
    zeit: zeile,
    regler: starts.length > 1 ? { wert: i, max: starts.length - 1, text: zeile, label: "Vorlese-Position (Teil)" } : null,
    schritt: starts.length > 1 ? { art: "abschnitt", zurueck: "Teil zurück", vor: "Teil vor" } : null,
    umschalten: () => q.umschalten(),
    stopp: () => q.stoppe(),
    gehe: (r) => q.springeUm(r),
    setze: (wert) => q.springeZuAbschnitt(wert),
    zurKarte: () => q.zurKarte(),
  };
}

/**
 * Was die Leiste jetzt zeigt — Vorrang hat, WAS KLINGT: Leitstand klingt > Vorlesung lädt/spielt > Pausiertes (Leitstand,
 * dann Vorlesung). Ein pausierter Ton im Leitstand (audio-player.ts `melde(false)`, `pausiereFuerHand`) darf eine laufende
 * Vorlesung nicht verdrängen: Sonst hieße „Play“ in der Leiste, einen zweiten Ton neben der Vorlesung zu starten.
 */
function jetzigeAnsicht(): Ansicht | null {
  const stand = aktuellerTon();
  const q = aktiveVorleseQuelle();
  const vorleseKlingt = q !== null && (q.zustand() === "playing" || q.zustand() === "loading");
  if (stand?.klingt) return ansichtAusTon(stand);
  if (q && vorleseKlingt) return ansichtAusVorlesen(q);
  if (stand) return ansichtAusTon(stand);
  return q ? ansichtAusVorlesen(q) : null;
}

let ansicht: Ansicht | null = null;
/** Kurzer Hinweis statt der Zeit (z. B. die Antwort ist nicht mehr im Chat) — verschwindet von selbst. */
let hinweis: string | null = null;
let hinweisTimer: ReturnType<typeof setTimeout> | null = null;
const HINWEIS_MS = 3000;

function zeigeHinweis(text: string): void {
  hinweis = text;
  if (hinweisTimer !== null) clearTimeout(hinweisTimer);
  hinweisTimer = setTimeout(() => {
    hinweis = null;
    hinweisTimer = null;
    zeichneLeiste(jetzigeAnsicht());
  }, HINWEIS_MS);
  zeichneLeiste(jetzigeAnsicht());
}

function knopf(klasse: string, label: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = klasse;
  b.setAttribute("aria-label", label);
  return b;
}

/** Baut die Leiste einmalig und hängt sie vor das Dock. */
function baueLeiste(): HTMLElement {
  const el = document.createElement("div");
  el.className = "tonleiste";
  el.setAttribute("role", "group");
  el.setAttribute("aria-label", "Wiedergabe");
  el.hidden = true;

  const flaeche = document.createElement("button");
  flaeche.type = "button";
  flaeche.className = "tonleiste-flaeche";
  flaeche.setAttribute("aria-label", "Player öffnen");
  flaeche.innerHTML =
    '<span class="tonleiste-zeichen"></span>' +
    '<span class="tonleiste-titel"></span>' +
    '<span class="tonleiste-zeit"></span>';
  flaeche.addEventListener("click", () => {
    // Eine Vorlese-Quelle springt zur Antwort im Chat; alles andere öffnet den großen Player.
    if (!ansicht?.zurKarte) oeffneModal();
    else if (!ansicht.zurKarte()) zeigeHinweis("Antwort nicht mehr im Chat");
  });

  const zurueck = knopf("tonleiste-schritt tonleiste-schritt--vorher", "Zurück");
  zurueck.addEventListener("click", (e) => {
    e.stopPropagation();
    ansicht?.gehe(-1);
  });

  const schalter = document.createElement("button");
  schalter.type = "button";
  schalter.className = "tonleiste-schalter";
  schalter.addEventListener("click", (e) => {
    // Nicht die Fläche mit auslösen — sonst öffnet jeder Pausenklick das
    // Modal.
    e.stopPropagation();
    ansicht?.umschalten();
  });

  const schieber = document.createElement("input");
  schieber.type = "range";
  schieber.className = "player-bar-seek tonleiste-schieber";
  schieber.min = "0";
  schieber.step = "any";
  schieber.hidden = true;
  schieber.addEventListener("pointerdown", () => {
    schieberZieht = true;
  });
  const losgelassen = (): void => {
    schieberZieht = false;
  };
  schieber.addEventListener("pointerup", losgelassen);
  schieber.addEventListener("pointercancel", losgelassen);
  schieber.addEventListener("input", () => {
    const zeit = el.querySelector<HTMLElement>(".tonleiste-zeit");
    const r = ansicht?.regler;
    if (!r) return;
    // Vorschau, solange gezogen wird: bei Abschnitten die Nummer, bei Sekunden die Uhr.
    const wert = Number(schieber.value);
    const t =
      ansicht?.schritt?.art === "abschnitt"
        ? teilText(Math.round(wert), Math.round(r.max) + 1)
        : `${uhr(wert)} / ${uhr(r.max)}`;
    if (zeit) zeit.textContent = t;
    schieber.setAttribute("aria-valuetext", t);
    schiebeFuellung(schieber);
  });
  schieber.addEventListener("change", () => {
    schieberZieht = false;
    ansicht?.setze(Number(schieber.value));
  });

  const vor = knopf("tonleiste-schritt tonleiste-schritt--vor", "Vor");
  vor.addEventListener("click", (e) => {
    e.stopPropagation();
    ansicht?.gehe(1);
  });

  /*
   * Der Stopp-Knopf. (05.09.2026)
   *
   * Martin: *„Den kann man nicht auf Stopp drücken und man kann ihn auch
   * nicht schließen. Ist ja aber wichtig — Pause, Stopp und Schließen.“*
   *
   * Stopp UND Schließen sind hier dasselbe: Was nicht mehr klingt, hat auf
   * der Leiste nichts verloren — sie zeigt ausschließlich, was läuft. Ein
   * eigenes Kreuz daneben wäre ein zweiter Knopf für dieselbe Wirkung.
   */
  const stopp = document.createElement("button");
  stopp.type = "button";
  stopp.className = "tonleiste-stopp";
  stopp.setAttribute("aria-label", "Wiedergabe beenden");
  stopp.title = "Beenden";
  stopp.innerHTML = STOPP;
  stopp.addEventListener("click", (e) => {
    e.stopPropagation();
    ansicht?.stopp();
  });

  /*
   * „Mehr“ (21.10.2026, Martin: „Schau furchtbar wie unübersichtlich!!“ — der untere Chatbereich): Am Telefon steht der
   * Mini-Player in EINER Reihe — Titel mit Zeit/Abschnitt, Pause, Stopp. Zurück, Schieber und Vor klappen mit diesem Knopf
   * auf (style.css „Mini-Player einzeilig“); am Rechner (ab 768) steht ohnehin alles in einer Reihe, der Knopf fehlt dort.
   * Nur Anzeige: er schaltet eine Klasse, nichts an der Wiedergabe.
   */
  const mehr = knopf("tonleiste-mehr", "Mehr Steuerung: zurück, Schieber, vor");
  mehr.setAttribute("aria-expanded", "false");
  zurueck.id = "tonleiste-zurueck";
  schieber.id = "tonleiste-schieber";
  vor.id = "tonleiste-vor";
  mehr.setAttribute("aria-controls", `${zurueck.id} ${schieber.id} ${vor.id}`);
  mehr.innerHTML = MEHR;
  mehr.addEventListener("click", (e) => {
    e.stopPropagation();
    klappe(el, !el.classList.contains("tonleiste--offen"));
  });

  // DOM-Reihenfolge = Tab-Reihenfolge = was man sieht: erst die Reihe, die immer steht (Fläche, Pause, Stopp, „Mehr“),
  // dann, was „Mehr“ aufklappt (Zurück, Schieber, Vor) — vom aufgeklappten „Mehr“ führt Tab direkt hinein.
  el.append(flaeche, schalter, stopp, mehr, zurueck, schieber, vor);
  // Vor das Dock, damit sie über der Fußleiste sitzt und nichts verdeckt.
  const dock = document.querySelector(".dock");
  if (dock?.parentElement) dock.parentElement.insertBefore(el, dock);
  else document.body.appendChild(el);
  beobachteHoehe(el);
  return el;
}

/** „Mehr“ auf- oder zuklappen (nur Klasse und Ansage, nichts an der Wiedergabe). */
function klappe(el: HTMLElement, auf: boolean): void {
  el.classList.toggle("tonleiste--offen", auf);
  el.querySelector(".tonleiste-mehr")?.setAttribute("aria-expanded", String(auf));
}

/** Die gehörte Strecke färbt die Wellenform des Schiebers (style.css `--gehoert`). */
function schiebeFuellung(s: HTMLInputElement): void {
  const max = Number(s.max) || 1;
  s.style.setProperty("--gehoert", `${Math.max(0, Math.min(100, (Number(s.value) / max) * 100))}%`);
}

/**
 * Die Höhe der Leiste als `--tonleiste-h` (0, solange sie fehlt oder verborgen ist): Was unten schwebt (die Sprech-Pille),
 * hebt sich um genau so viel — sonst läge es auf dem Player.
 */
function setzeHoehe(): void {
  if (!leiste) return;
  document.documentElement.style.setProperty("--tonleiste-h", `${leiste.hidden ? 0 : leiste.offsetHeight}px`);
}

function beobachteHoehe(el: HTMLElement): void {
  try {
    hoehenBeobachter?.disconnect();
    hoehenBeobachter = new ResizeObserver(setzeHoehe);
    hoehenBeobachter.observe(el, { box: "border-box" });
  } catch {
    /* ohne ResizeObserver misst `zeichneLeiste` bei jeder Zeichnung */
  }
}

/** Schreibt eine Ansicht in die Leiste. `null` blendet sie aus. */
function zeichneLeiste(a: Ansicht | null): void {
  ansicht = a;
  if (!leiste) return;
  if (!a) {
    // Ohne Wiedergabe gibt es nichts mehr, wozu der Hinweis gehörte.
    hinweis = null;
    if (hinweisTimer !== null) clearTimeout(hinweisTimer);
    hinweisTimer = null;
    // Endet die Wiedergabe, beginnt die nächste wieder zugeklappt.
    klappe(leiste, false);
    delete leiste.dataset.quelle;
    leiste.hidden = true;
    setzeHoehe();
    return;
  }
  leiste.hidden = false;
  // Wechselt die Wiedergabe (andere Art oder anderer Titel), beginnt auch sie zugeklappt.
  const quelle = `${a.art}|${a.titel}`;
  if (leiste.dataset.quelle !== undefined && leiste.dataset.quelle !== quelle) klappe(leiste, false);
  leiste.dataset.quelle = quelle;
  leiste.dataset.art = a.art;
  const z = leiste.querySelector<HTMLElement>(".tonleiste-zeichen");
  const t = leiste.querySelector<HTMLElement>(".tonleiste-titel");
  const zeit = leiste.querySelector<HTMLElement>(".tonleiste-zeit");
  const schalter = leiste.querySelector<HTMLElement>(".tonleiste-schalter");
  const flaeche = leiste.querySelector<HTMLElement>(".tonleiste-flaeche");
  if (z) z.innerHTML = ZEICHEN[a.art];
  if (t) t.textContent = a.titel;
  if (zeit && !schieberZieht) {
    zeit.textContent = hinweis ?? a.zeit;
    // Der Hinweis wird angesagt, die laufende Zeit nicht (sie tickt jede Sekunde).
    if (hinweis !== null) zeit.setAttribute("aria-live", "polite");
    else zeit.removeAttribute("aria-live");
  }
  if (flaeche) {
    const ziel = a.zurKarte ? "Zur Antwort im Chat springen" : "Player öffnen";
    flaeche.setAttribute("aria-label", `${ANSAGE[a.art]}: ${a.titel}. ${ziel}`);
    flaeche.title = ziel;
  }
  if (schalter) {
    const wort = a.laedt ? "Abbrechen" : a.klingt ? "Anhalten" : "Fortsetzen";
    schalter.innerHTML = a.laedt ? SPINNER : a.klingt ? PAUSE : PLAY;
    schalter.setAttribute("aria-label", `${ANSAGE[a.art]} ${wort}`);
    schalter.title = wort;
    // Eine Quelle ohne Fortsetzen (etwa ein Anruf) darf keinen toten Knopf
    // zeigen.
    schalter.hidden = !a.klingt && !a.laedt && !a.fortsetzbar;
  }
  const schieber = leiste.querySelector<HTMLInputElement>(".tonleiste-schieber");
  if (schieber) {
    schieber.hidden = !a.regler;
    if (a.regler) {
      const r = a.regler;
      schieber.max = String(r.max);
      // Gesprungen wird erst beim Loslassen; bei Abschnitten in ganzen Schritten.
      schieber.step = a.schritt?.art === "abschnitt" ? "1" : "any";
      schieber.setAttribute("aria-label", r.label);
      if (!schieberZieht && document.activeElement !== schieber) {
        schieber.value = String(r.wert);
        schieber.setAttribute("aria-valuetext", r.text);
        schiebeFuellung(schieber);
      }
    }
  }
  const schritte = [
    [leiste.querySelector<HTMLButtonElement>(".tonleiste-schritt--vorher"), -1],
    [leiste.querySelector<HTMLButtonElement>(".tonleiste-schritt--vor"), 1],
  ] as const;
  for (const [k, richtung] of schritte) {
    if (!k) continue;
    k.hidden = !a.schritt;
    if (!a.schritt) continue;
    const label = richtung < 0 ? a.schritt.zurueck : a.schritt.vor;
    k.setAttribute("aria-label", label);
    k.title = label;
    k.dataset.art = a.schritt.art;
    k.innerHTML =
      a.schritt.art === "abschnitt"
        ? richtung < 0
          ? ABSCHNITT_ZURUECK
          : ABSCHNITT_VOR
        : `<span class="tonleiste-schritt-zahl">${richtung < 0 ? "−" : "+"}${SCHRITT_SEK}</span>`;
  }
  const mitBedienung = a.regler !== null || a.schritt !== null;
  leiste.classList.toggle("tonleiste--regler", mitBedienung);
  // „Mehr“ nur, wenn es etwas aufzuklappen gibt (Schieber oder Schritte).
  const mehr = leiste.querySelector<HTMLButtonElement>(".tonleiste-mehr");
  if (mehr) mehr.hidden = !mitBedienung;
  setzeHoehe();
}

/** Der große Player. Wird beim ersten Öffnen gebaut. */
function baueModal(): HTMLElement {
  const el = document.createElement("div");
  el.className = "sheet sheet--modal tonmodal";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-label", "Abspieler");
  el.hidden = true;
  el.innerHTML =
    '<div class="sheet-backdrop" data-ton-close></div>' +
    '<div class="sheet-card tonmodal-card">' +
    '  <div class="tonmodal-art"></div>' +
    '  <strong class="tonmodal-titel"></strong>' +
    '  <div class="tonmodal-zeit"></div>' +
    '  <div class="tonmodal-knoepfe">' +
    '    <button type="button" class="tonmodal-sprung" data-sek="-15" aria-label="15 Sekunden zurück">−15</button>' +
    '    <button type="button" class="tonmodal-schalter"></button>' +
    '    <button type="button" class="tonmodal-sprung" data-sek="15" aria-label="15 Sekunden vor">+15</button>' +
    "  </div>" +
    '  <button type="button" class="btn btn--ghost btn--sm tonmodal-stopp">Beenden</button>' +
    '  <button type="button" class="btn tonmodal-fertig" data-ton-close>Fertig</button>' +
    "</div>";

  el.querySelectorAll("[data-ton-close]").forEach((k) =>
    k.addEventListener("click", schliesseModal),
  );
  el.querySelector(".tonmodal-schalter")?.addEventListener("click", () => {
    const s = aktuellerTon();
    if (s?.klingt) halteAn();
    else setzeFort();
  });
  el.querySelector(".tonmodal-stopp")?.addEventListener("click", () => {
    // Beenden schliesst auch das Fenster — `zeichneModal(null)` tut das,
    // sobald die Buehne leer ist.
    beende();
  });
  el.querySelectorAll<HTMLElement>(".tonmodal-sprung").forEach((k) =>
    k.addEventListener("click", () => springe(Number(k.dataset.sek ?? 0))),
  );
  document.body.appendChild(el);
  return el;
}

function zeichneModal(stand: TonStand | null): void {
  if (!modal || modal.hidden) return;
  // Was nicht mehr klingt, braucht kein großes Fenster.
  if (!stand) {
    schliesseModal();
    return;
  }
  const art = modal.querySelector<HTMLElement>(".tonmodal-art");
  const titel = modal.querySelector<HTMLElement>(".tonmodal-titel");
  const zeit = modal.querySelector<HTMLElement>(".tonmodal-zeit");
  const schalter = modal.querySelector<HTMLElement>(".tonmodal-schalter");
  if (art) art.textContent = ANSAGE[stand.art];
  if (titel) titel.textContent = stand.titel;
  if (zeit) zeit.textContent = zeitText(stand) || "läuft";
  if (schalter) {
    const wort = stand.klingt ? "Anhalten" : "Fortsetzen";
    schalter.innerHTML = stand.klingt ? PAUSE : PLAY;
    schalter.setAttribute("aria-label", wort);
    schalter.title = wort;
  }
  modal
    .querySelectorAll<HTMLElement>(".tonmodal-sprung")
    .forEach((k) => (k.hidden = !stand.springbar));
}

export function oeffneModal(): void {
  if (!aktuellerTon()) return;
  // ⚠️ Nicht `??=`: Ein Element, das einmal gebaut wurde, kann aus dem Baum
  //    verschwinden (eine Ansicht ersetzt den Rumpf). Dann zeigte das Modal
  //    ins Leere — sichtbar für niemanden, aber „offen" für den Code.
  if (!modal?.isConnected) modal = baueModal();
  modal.hidden = false;
  zeichneModal(aktuellerTon());
}

export function schliesseModal(): void {
  if (modal) modal.hidden = true;
}

/**
 * Leiste und Modal an den Leitstand hängen. Einmal beim Start rufen.
 *
 * Der Ticker läuft NUR, solange etwas klingt — eine Uhr, die im Leerlauf
 * jede Sekunde aufwacht, ist genau die Art von Kleinigkeit, die auf einem
 * Telefon den Akku frisst.
 */
export function starteTonLeiste(): void {
  if (!leiste?.isConnected) leiste = baueLeiste();
  // Beide Quellen zeichnen dieselbe Leiste. Eine Zeichnung liest immer den ganzen aktuellen Stand neu (Leitstand
  // zuerst), sie hängt also nicht davon ab, welche Quelle sie gerufen hat.
  const zeichneAlles = (): void => {
    // Dasselbe wie beim Modal: Wer den Rumpf neu baut, nimmt die Leiste mit.
    // Sie kommt dann beim nächsten Ton von selbst zurück, statt stumm zu
    // bleiben.
    if (!leiste?.isConnected) leiste = baueLeiste();
    zeichneLeiste(jetzigeAnsicht());
    zeichneModal(aktuellerTon());
  };
  // Die Uhr tickt, solange der Leitstand ODER die Vorlese-Quelle spielt (deren Zeit seit Start läuft von selbst weiter).
  const steuereUhr = (): void => {
    const soll = Boolean(aktuellerTon()?.klingt) || aktiveVorleseQuelle()?.zustand() === "playing";
    if (soll && ticker === null) {
      ticker = setInterval(gesteuert("live", zeichneAlles, true), 1000);
    } else if (!soll && ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
  };
  beobachteVorlesen(() => {
    zeichneAlles();
    steuereUhr();
  });
  beobachte(() => {
    zeichneAlles();
    steuereUhr();
  });
}

/** Nur Tests. */
export function _leiste(): HTMLElement | null {
  return leiste;
}
